import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import BlayzBot from './components/dashboard/BlayzBot';
import TrackingScriptsInjector from './components/TrackingScriptsInjector';
import { AuthProvider, useAuth } from './lib/auth-context';
import { initReferralTracking, trackPath } from './lib/referralTracker';
import LandingPage from './pages/LandingPage';
import FreeScoreLandingPage from './pages/FreeScoreLandingPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import DashboardPage from './pages/DashboardPage';
import ManualWizardPage from './pages/ManualWizardPage';
import AnalysisPage from './pages/AnalysisPage';
import CarouselDesignerPage from './pages/CarouselDesignerPage';
import SettingsPage from './pages/SettingsPage';
import PreferencesPage from './pages/PreferencesPage';
import AdminPage from './pages/AdminPage';
import AdminDiscoverPage from './pages/AdminDiscoverPage';
import AdminSupportPage from './pages/AdminSupportPage';
import AdminApifyUsagePage from './pages/AdminApifyUsagePage';
import AdminHeroLeadsPage from './pages/AdminHeroLeadsPage';
import AdminFreeLeadsPage from './pages/AdminFreeLeadsPage';
import AdminSupportInboxPage from './pages/AdminSupportInboxPage';
import MethodologyPage from './pages/MethodologyPage';
import PricingPage from './pages/PricingPage';
import BillingPage from './pages/BillingPage';
import BillingSuccessPage from './pages/BillingSuccessPage';
import ThankYouPage from './pages/ThankYouPage';
import RefundPolicyPage from './pages/RefundPolicyPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import ContactPage from './pages/ContactPage';
import ProfileSuccessFeaturePage from './pages/ProfileSuccessFeaturePage';
import ThoughtLeadershipFeaturePage from './pages/ThoughtLeadershipFeaturePage';
import HomeV2Page from './pages/HomeV2Page';
import RecruiterVisibilityPage from './pages/RecruiterVisibilityPage';
import FreeGiveawayPage from './pages/FreeGiveawayPage';
import AdminReportsPage from './pages/AdminReportsPage';
import AdminEventsPage from './pages/AdminEventsPage';
import MagicLinkPage from './pages/MagicLinkPage';
import { QuotaExceededDialog } from './components/billing/QuotaExceededDialog';
import { TrackingInjector, CookieConsentBanner, CookiePreferencesPill } from './components/TrackingInjector';
import './App.css';

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (user) {
    // Honor ?next= so post-signup / post-login flows (e.g. the free-score
    // CTA) can steer the user somewhere other than /dashboard — most
    // importantly, straight to /pricing so a fresh score can convert.
    // Reject anything that isn't a safe internal path.
    const params = new URLSearchParams(location.search);
    const raw = params.get('next') || '';
    const nextPath = raw && raw.startsWith('/') && !raw.startsWith('//')
      ? raw
      : '/dashboard';
    return <Navigate to={nextPath} replace />;
  }
  return children;
}

/**
 * Boots referral tracking on first mount and keeps last_path fresh on
 * every route change so signups/checkouts can attribute correctly.
 */
function ReferralTrackerBoot() {
  const location = useLocation();
  React.useEffect(() => {
    initReferralTracking();
  }, []);
  React.useEffect(() => {
    trackPath(location.pathname + (location.search || ''));
  }, [location.pathname, location.search]);
  return null;
}

/**
 * Resets the window scroll to the top on every route change so users
 * don't land on the bottom of a new page when they navigated away from
 * a scrolled-down source page (e.g. clicking Pricing from the middle of
 * the landing page previously left the user at the bottom of /pricing).
 *
 * If the URL carries a hash (#plans, etc.) we defer to the browser's
 * native anchor-jump so in-page anchors still work.
 */
function ScrollToTopOnRouteChange() {
  const location = useLocation();
  React.useEffect(() => {
    if (location.hash) return;
    window.scrollTo(0, 0);
  }, [location.pathname, location.search, location.hash]);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ReferralTrackerBoot />
        <ScrollToTopOnRouteChange />
        <div className="App">
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/home" element={<HomeV2Page />} />
            <Route path="/recruiter-visibility" element={<RecruiterVisibilityPage />} />
            <Route path="/free" element={<FreeGiveawayPage />} />
            <Route path="/features/profile-success" element={<ProfileSuccessFeaturePage />} />
            <Route path="/features/thought-leadership" element={<ThoughtLeadershipFeaturePage />} />
            <Route path="/free-score" element={<FreeScoreLandingPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/auth/magic" element={<MagicLinkPage />} />
            <Route path="/get-score" element={<FreeScoreLandingPage />} />
            <Route path="/lp/score" element={<FreeScoreLandingPage />} />
            <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
            <Route path="/signup" element={<PublicOnly><SignupPage /></PublicOnly>} />
            <Route path="/dashboard" element={<Protected><DashboardPage /></Protected>} />
            <Route path="/wizard" element={<Protected><ManualWizardPage /></Protected>} />
            <Route path="/analysis/:id" element={<Protected><AnalysisPage /></Protected>} />
            <Route path="/carousel-designer" element={<Protected><CarouselDesignerPage /></Protected>} />
            <Route path="/settings" element={<Protected><SettingsPage /></Protected>} />
            <Route path="/preferences" element={<Protected><PreferencesPage /></Protected>} />
            <Route path="/admin" element={<Protected><AdminPage /></Protected>} />
            <Route path="/admin/support" element={<Protected><AdminSupportPage /></Protected>} />
            <Route path="/admin/apify-usage" element={<Protected><AdminApifyUsagePage /></Protected>} />
            <Route path="/admin/hero-leads" element={<Protected><AdminHeroLeadsPage /></Protected>} />
            <Route path="/admin/free-leads" element={<Protected><AdminFreeLeadsPage /></Protected>} />
            <Route path="/admin/discover" element={<Protected><AdminDiscoverPage /></Protected>} />
            <Route path="/admin/support-inbox" element={<Protected><AdminSupportInboxPage /></Protected>} />
            <Route path="/admin/reports" element={<Protected><AdminReportsPage /></Protected>} />
            <Route path="/admin/events" element={<Protected><AdminEventsPage /></Protected>} />
            <Route path="/methodology" element={<MethodologyPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/billing" element={<Protected><BillingPage /></Protected>} />
            <Route path="/billing/success" element={<Protected><BillingSuccessPage /></Protected>} />
            {/* Region-scoped thank-you pages so GA4 URL-based conversion
                goals can segment US vs India buyers. Public route
                because guest-checkout buyers hit them before their
                account is provisioned. */}
            <Route path="/thank-you/:region" element={<ThankYouPage />} />
            <Route path="/refund-policy" element={<RefundPolicyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/support" element={<ContactPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <QuotaExceededDialog />
          <TrackingInjector />
          <CookieConsentBanner />
          <CookiePreferencesPill />
          <Toaster richColors closeButton position="top-right" />
          <BlayzBotGlobalHost />
          <TrackingScriptsInjector />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

// Route-aware Blayz mount. Dashboard + Analysis pages already mount a
// context-rich BlayzBot with per-page state (uploading / consent /
// auditReady) so we skip mounting a second instance there. Everywhere
// else we surface Blayz with an empty tip list — the mascot opens
// straight into the AI chat via the "Ask Blayz anything" CTA.
function BlayzBotGlobalHost() {
  const { pathname } = useLocation();
  const skip = pathname === '/dashboard'
    || pathname.startsWith('/analysis/')
    || pathname.startsWith('/admin');
  if (skip) return null;
  return <BlayzBot context="global" state={{}} />;
}

export default App;
